import { equal } from '@/api/helpers';
import { Campaign, CampaignStatusesEnum } from '@/client/campaigns';
import {
  ActiveCampaignBanner,
  EmptyCampaignBanner,
  ScheduledCampaignBanner,
} from '@/components/dashboards/admin';
import { useCampaigns } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import React, { useEffect, useState } from 'react';

export const CampaignStatusBanner: React.FC = () => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [currentCampaign, setCurrentCampaign] = useState<Campaign | undefined>(
    undefined,
  );

  const { isLoading: isLoadingActiveCampaigns, campaigns: activeCampaigns } =
    useCampaigns({
      take: 1,
      skip: 0,
      filters: [
        equal('account', currentAccount?.id),
        equal('status', CampaignStatusesEnum.ACTIVE),
      ],
      sort: ['launchDate,asc'],
      enabled: !!currentAccount,
    });

  const {
    isLoading: isLoadingScheduledCampaigns,
    campaigns: scheduledCampaigns,
  } = useCampaigns({
    take: 1,
    skip: 0,
    filters: [
      equal('account', currentAccount?.id),
      equal('status', CampaignStatusesEnum.SCHEDULED),
    ],
    sort: ['launchDate,asc'],
    enabled: !!currentAccount,
  });

  useEffect(() => {
    if (activeCampaigns?.result?.[0]) {
      setCurrentCampaign(activeCampaigns.result[0]);
    } else if (scheduledCampaigns?.result?.[0]) {
      setCurrentCampaign(scheduledCampaigns.result[0]);
    } else {
      setCurrentCampaign(undefined);
    }
  }, [
    activeCampaigns,
    scheduledCampaigns,
    isLoadingActiveCampaigns,
    isLoadingScheduledCampaigns,
  ]);

  const isLoadingCampaigns =
    isLoadingActiveCampaigns || isLoadingScheduledCampaigns;

  if (isLoadingCampaigns) {
    return null;
  }
  if (!currentCampaign) {
    return <EmptyCampaignBanner />;
  }

  if (currentCampaign.status === CampaignStatusesEnum.ACTIVE) {
    return <ActiveCampaignBanner initialCampaign={currentCampaign} />;
  }

  if (currentCampaign.status === CampaignStatusesEnum.SCHEDULED) {
    return <ScheduledCampaignBanner initialCampaign={currentCampaign} />;
  }

  return null;
};
